import React from "react";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { CheckCircle2, Quote } from "lucide-react";

const FN_LOGO =
  "https://customer-assets.emergentagent.com/job_premium-makhana-11/artifacts/4l1hs9ak_FN%20Logo%20%281%29.png";
const LOTUS_IMG =
  "https://images.pexels.com/photos/31845376/pexels-photo-31845376.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const values = [
  "Sourced directly from Bihar's finest lotus ponds — zero middlemen",
  "Strict Suta-based grading for guaranteed size, purity & freshness",
  "100% replacement guarantee on every order — no questions asked",
  "GST-registered company with transparent, all-inclusive pricing",
  "B2B wholesale and D2C retail support across India",
  "International export capability with compliance certifications",
];

const AboutSection = () => {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section id="about" ref={ref} className="py-24 bg-[#0A0A0A]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side */}
          <div
            className="relative order-2 lg:order-1"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateX(0)" : "translateX(-40px)",
              transition: "opacity 0.8s ease, transform 0.8s ease",
            }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_80px_rgba(0,0,0,0.5)]">
              <img
                src={LOTUS_IMG}
                alt="Bihar Lotus Pond — Origin of Makhana"
                className="w-full h-[480px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/90 via-[#0A0A0A]/20 to-transparent" />
              {/* Caption overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="border-l-2 border-[#D4AF37] pl-4">
                  <p className="text-[#D4AF37] text-xs uppercase tracking-[3px] mb-1">
                    Bihar's Sacred Ponds
                  </p>
                  <p
                    className="text-[#FAFAFA] text-lg font-semibold leading-snug"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Where Every MakhanaG Journey Begins
                  </p>
                </div>
              </div>
            </div>

            {/* Floating logo card */}
            <div className="absolute -top-5 -right-5 bg-[#121212] border border-[#D4AF37]/30 rounded-2xl p-4 shadow-[0_0_40px_rgba(212,175,55,0.2)]">
              <img
                src={FN_LOGO}
                alt="New Foxnourish"
                className="w-16 h-16 rounded-full animate-pulse-gold"
              />
            </div>

            {/* Gold corner accent */}
            <div className="absolute -bottom-3 -left-3 w-24 h-24 border-b-2 border-l-2 border-[#D4AF37]/30 rounded-bl-2xl pointer-events-none" />
          </div>

          {/* Text Side */}
          <div
            className="order-1 lg:order-2"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateX(0)" : "translateX(40px)",
              transition: "opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s",
            }}
          >
            <span className="text-[#D4AF37] text-xs uppercase tracking-[5px] mb-4 block font-medium">
              Our Heritage
            </span>
            <h2
              className="text-4xl md:text-5xl font-bold text-[#FAFAFA] mb-6 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="about-heading"
            >
              Bihar's Legacy,
              <br />
              <span className="text-[#D4AF37]">Delivered to You</span>
            </h2>

            <p className="text-[#A1A1AA] text-base leading-relaxed mb-5">
              <span className="text-[#FAFAFA] font-semibold">MakhanaG</span> is
              the flagship premium brand of{" "}
              <span className="text-[#D4AF37] font-semibold">
                New Foxnourish Private Limited
              </span>{" "}
              — a company deeply rooted in the heart of Bihar, India.
            </p>

            <p className="text-[#A1A1AA] text-base leading-relaxed mb-8">
              For centuries, the sacred lotus ponds of Mithila have yielded the
              world's finest fox nuts (Makhana). We honor that ancient tradition
              by bringing you makhana that is pure, traceable, and graded with
              the precision of the time-honored{" "}
              <span className="text-[#FAFAFA]">Suta system</span>.
            </p>

            {/* Values Checklist */}
            <div className="space-y-3 mb-8">
              {values.map((val, i) => (
                <div key={i} className="flex items-start gap-3 group">
                  <CheckCircle2
                    size={17}
                    className="text-[#D4AF37] mt-0.5 shrink-0"
                  />
                  <span className="text-[#A1A1AA] text-sm leading-relaxed group-hover:text-[#FAFAFA] transition-colors duration-200">
                    {val}
                  </span>
                </div>
              ))}
            </div>

            {/* Quality Promise Quote */}
            <div className="relative bg-[#121212] border border-[#D4AF37]/20 rounded-2xl p-6">
              <Quote className="absolute top-4 right-4 text-[#D4AF37]/20" size={40} />
              <p
                className="text-[#FAFAFA] text-base italic leading-relaxed mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                "If our Makhana doesn't meet your expectations — whether it's
                1 kg or 1,000 kg — we replace the entire lot. No questions
                asked."
              </p>
              <div className="flex items-center gap-3">
                <img
                  src={FN_LOGO}
                  alt="New Foxnourish"
                  className="w-8 h-8 rounded-full"
                />
                <div>
                  <p className="text-[#D4AF37] text-sm font-semibold">Team MakhanaG</p>
                  <p className="text-[#525252] text-xs">New Foxnourish Private Limited</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
